// Strava API integration
const STRAVA_CLIENT_ID = import.meta.env.VITE_STRAVA_CLIENT_ID || '9550';
const STRAVA_CLIENT_SECRET = import.meta.env.VITE_STRAVA_CLIENT_SECRET || '33477d75e43e615bc400a571bb29f5dd88649e46';

// Use the exact redirect URI that's registered in the Strava API settings
const getRedirectUri = () => {
  const isDevelopment = window.location.hostname === 'localhost';
  
  // Use the exact redirect URI that's registered in your Strava application
  // For local development, you may need to register a separate application or update your existing one
  return isDevelopment 
    ? 'http://localhost:5173'  // You might need to register this separately
    : 'https://runtler.com';    // This must exactly match what's in Strava API settings
};

// Authorization URL construction
export const getStravaAuthUrl = () => {
  const scope = 'read,activity:read_all';
  const state = Math.random().toString(36).substring(2, 15);
  const redirectUri = getRedirectUri();
  console.log('Generating Strava auth URL with redirect:', redirectUri);
  return `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&approval_prompt=force&scope=${scope}&state=${state}`;
};

// Token exchange
export const exchangeToken = async (code) => {
  try {
    const redirectUri = getRedirectUri();
    console.log('Exchanging token with:', {
      client_id: STRAVA_CLIENT_ID,
      redirect_uri: redirectUri,
      code: code
    });
    const response = await fetch('https://www.strava.com/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: STRAVA_CLIENT_ID,
        client_secret: STRAVA_CLIENT_SECRET,
        code,
        grant_type: 'authorization_code',
        redirect_uri: redirectUri,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Token exchange error response:', errorData);
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Token exchange successful:', {
      access_token: data.access_token ? 'present' : 'missing',
      expires_at: data.expires_at,
      athlete: data.athlete?.firstname
    });
    return data;
  } catch (error) {
    console.error('Token exchange error:', error);
    throw error;
  }
};

// Rest of the file remains unchanged
export const refreshStravaToken = async (refresh_token) => {
  try {
    const response = await fetch('https://www.strava.com/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: STRAVA_CLIENT_ID,
        client_secret: STRAVA_CLIENT_SECRET,
        refresh_token,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Token refresh error response:', errorData);
      throw new Error(errorData.message || 'Failed to refresh token');
    }

    return await response.json();
  } catch (error) {
    console.error('Token refresh error:', error);
    throw error;
  }
};

// Fetch athlete profile
export const fetchAthleteProfile = async (access_token) => {
  try {
    const response = await fetch('https://www.strava.com/api/v3/athlete', {
      headers: {
        'Authorization': `Bearer ${access_token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Fetch athlete error:', errorData);
      throw new Error(errorData.message || 'Failed to fetch athlete profile');
    }

    return await response.json();
  } catch (error) {
    console.error('Fetch athlete error:', error);
    throw error;
  }
};

// Fetch activities
export const fetchActivities = async (access_token, page = 1, per_page = 20) => {
  try {
    const response = await fetch(`https://www.strava.com/api/v3/athlete/activities?page=${page}&per_page=${per_page}`, {
      headers: {
        'Authorization': `Bearer ${access_token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Fetch activities error:', errorData);
      throw new Error(errorData.message || 'Failed to fetch activities');
    }

    return await response.json();
  } catch (error) {
    console.error('Fetch activities error:', error);
    throw error;
  }
};

// Fetch activity details
export const fetchActivityDetails = async (access_token, activityId) => {
  try {
    const response = await fetch(`https://www.strava.com/api/v3/activities/${activityId}?include_all_efforts=true`, {
      headers: {
        'Authorization': `Bearer ${access_token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error(`Fetch activity details error for ID ${activityId}:`, errorData);
      throw new Error(errorData.message || 'Failed to fetch activity details');
    }

    return await response.json();
  } catch (error) {
    console.error(`Fetch activity details error for ID ${activityId}:`, error);
    throw error;
  }
};